// App shell. Two-level navigation: a rail of sections on the far left, a panel listing the pages
// of whichever section you are in, and a top bar carrying the things that are true of the whole
// app — which company you are in, where you are, search, New, and you.
//
// The prototype (`web/src/components/Shell.tsx`) had one flat column. With every module licensed
// that column reached ~48 links under nine headings and Accounting alone ran to fifteen, so the
// list you wanted was usually below the fold. Sections and items both come from module plugins;
// `modules.ts` decides the order and the icon of each section.
import {
  api,
  type DashboardBadges,
  initials,
  type NavSection,
  navSections,
  type PluginNavItem,
  sectionForPath,
  slotEntries,
  type TenantSummary,
  t,
  useApi,
  useAuth,
} from "@keel/core";
import { type Crumb, CrumbCtx, Icon, type Note, Notes, type RecordRef, useCurrentRecord } from "@keel/ui";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { CommandPalette } from "./CommandPalette";
import { HelpDrawer, useUnseen, WhatsNewPopup } from "./help";
import { activePlugins, sectionIcon, sectionRank } from "./modules";

/** Closes a popover when the pointer goes down outside it. */
function useDismiss(close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [close]);
  return ref;
}

function CompanySwitcher() {
  const { me, company, switchCompany, switchTenant } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(() => setOpen(false));
  const tenants = useQuery({
    queryKey: ["my-tenants"],
    queryFn: () => api<TenantSummary[]>("/auth/tenants"),
    staleTime: 60_000,
    enabled: open,
  });
  const others = (tenants.data ?? []).filter((x) => x.id !== me.tenant?.id);
  const switchable = me.companies.length > 1 || others.length > 0;
  return (
    <div className="company-switch" ref={ref}>
      <button
        type="button"
        className="company-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`${company.name} · ${me.tenant?.name ?? ""}`}
      >
        <span className="avatar co-avatar">{company.code}</span>
        <span className="brand-text">
          <b>{company.name}</b>
          <small>{me.tenant?.name}</small>
        </span>
        {switchable && <Icon name="updown" size={14} />}
      </button>
      {open && switchable && (
        <div className="company-menu" role="menu">
          <div className="nav-label" style={{ padding: "6px 10px 4px" }}>
            {t("Legal entities")}
          </div>
          {me.companies.map((c) => (
            <button
              type="button"
              key={c.id}
              role="menuitem"
              className={c.parentCompanyId ? "sub-co" : ""}
              onClick={() => {
                switchCompany(c.id);
                setOpen(false);
              }}
            >
              <span className="avatar" style={{ width: 24, height: 24, fontSize: 10 }}>
                {c.code}
              </span>
              <span>
                <div>{c.name}</div>
                <div className="muted">
                  {c.country} · {c.currency}
                  {c.parentCompanyId ? " · subsidiary" : " · parent"}
                </div>
              </span>
              {c.id === company.id && (
                <span className="check-mark">
                  <Icon name="check" size={14} />
                </span>
              )}
            </button>
          ))}
          {others.length > 0 && (
            <>
              <hr />
              <div className="nav-label" style={{ padding: "6px 10px 4px" }}>
                {t("Other accounts")}
              </div>
              {others.map((x) => (
                <button
                  type="button"
                  key={x.id}
                  role="menuitem"
                  className="tenant-row"
                  onClick={async () => {
                    setOpen(false);
                    await switchTenant(x.id);
                  }}
                >
                  <span className="avatar" style={{ width: 24, height: 24, fontSize: 10 }}>
                    {initials(x.name)}
                  </span>
                  <span>
                    <div>{x.name}</div>
                    <div className="muted mono" style={{ fontSize: 11 }}>
                      {x.slug}
                    </div>
                  </span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/** The top bar's "?": the help article for this page (ERP-66). "?" on the keyboard opens it too. */
/**
 * Notes on the record this page shows (ERP-72), in the same place on every record page: an
 * invoice, a contract, a customer. Hidden for people who may not read the record's notes.
 */
function NotesButton({ record, onOpen }: { record: RecordRef; onOpen: () => void }) {
  const q = useApi<Note[]>(`/notes?entityType=${record.entityType}&entityId=${record.entityId}`);
  if (q.error) return null;
  const n = q.data?.length ?? 0;
  return (
    <button
      type="button"
      className="btn btn-sm notes-btn"
      onClick={onOpen}
      title="Internal notes and tasks on this record"
      aria-label={n ? `Notes (${n})` : "Notes"}
    >
      <Icon name="comment" size={15} />
      <span className="notes-btn-label">Notes</span>
      {n > 0 && <span className="notes-btn-count">{n}</span>}
    </button>
  );
}

function NotesDrawer({ record, onClose }: { record: RecordRef; onClose: () => void }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) =>
      e.key === "Escape" && !document.querySelector("dialog[open]") && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <>
      <button type="button" className="drawer-scrim" aria-label="Close" onClick={onClose} />
      <aside className="drawer" aria-label="Notes">
        <div className="drawer-head">
          <div>
            <h2>{record.label ? `Notes · ${record.label}` : "Notes"}</h2>
            <p className="sub muted" style={{ textTransform: "none" }}>
              Internal — never printed. Also shown on the customer or supplier involved.
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </div>
        <div className="drawer-body">
          <Notes entityType={record.entityType} entityId={record.entityId} label={record.label} title="" />
        </div>
      </aside>
    </>
  );
}

function HelpButton({ onOpen }: { onOpen: () => void }) {
  const { unseen } = useUnseen();
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      const busy =
        el.closest("input, textarea, select, [contenteditable]") || document.querySelector("dialog[open]");
      if (e.key !== "?" || e.metaKey || e.ctrlKey || busy) return;
      e.preventDefault();
      onOpen();
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onOpen]);
  return (
    <button
      type="button"
      className="icon-btn"
      style={{ position: "relative" }}
      onClick={onOpen}
      aria-label={unseen.length ? `${t("Help for this page")} · ${t("What's new")}` : t("Help for this page")}
      title={`${t("Help for this page")} (?)`}
    >
      <Icon name="help" size={17} />
      {unseen.length > 0 && <span className="new-dot" />}
    </button>
  );
}

function UserMenu({ dark, toggleTheme }: { dark: boolean; toggleTheme: () => void }) {
  const { me, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(() => setOpen(false));
  const name = me.user?.name ?? me.actor;
  return (
    <div className="user-menu" ref={ref}>
      <button
        type="button"
        className="avatar-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={name}
      >
        <span className="avatar">{initials(name)}</span>
      </button>
      {open && (
        <div className="menu user-pop" role="menu">
          <div className="user-pop-head">
            <b>{name}</b>
            <small>{me.user?.role}</small>
          </div>
          <Link to="/account" role="menuitem" onClick={() => setOpen(false)}>
            <Icon name="user" size={15} />
            {t("My account")}
          </Link>
          <Link to="/help" role="menuitem" onClick={() => setOpen(false)}>
            <Icon name="help" size={15} />
            {t("Help")}
          </Link>
          <Link to="/whats-new" role="menuitem" onClick={() => setOpen(false)}>
            <Icon name="sparkle" size={15} />
            {t("What's new")}
          </Link>
          <button type="button" role="menuitem" onClick={toggleTheme}>
            <Icon name={dark ? "sun" : "moon"} size={15} />
            {t(dark ? "Light mode" : "Dark mode")}
          </button>
          <hr />
          <button type="button" role="menuitem" onClick={logout}>
            <Icon name="logout" size={15} />
            {t("Sign out")}
          </button>
        </div>
      )}
    </div>
  );
}

const SECTION_META = { rank: sectionRank, icon: sectionIcon };

/** The sections this user can see, in `modules.ts` order, each already filtered by permission. */
function useSections(): NavSection[] {
  const { me, can } = useAuth();
  const operator = !!me.operator;
  const modules = me.modules;
  return useMemo(() => navSections(modules, can, operator, SECTION_META), [modules, operator, can]);
}

/** Renders a module's self-computed nav counter; a component so the hook keeps a stable slot. */
function HookBadge({ use }: { use: () => { n: number; tone?: string } | null }) {
  const b = use();
  return b ? <span className={`nav-badge ${b.tone ?? ""}`}>{b.n}</span> : null;
}

function HookDot({ use }: { use: () => { n: number; tone?: string } | null }) {
  const b = use();
  return b ? <span className={`rail-dot ${b.tone ?? ""}`} /> : null;
}

/**
 * "Something in here wants you" — a dot rather than a number, because a section's counters come
 * from different modules and adding them together would mean nothing.
 */
function SectionDot({ items, data }: { items: PluginNavItem[]; data?: DashboardBadges }) {
  // One child per hook-badged item: each hook needs its own component to keep its slot stable.
  const hooked = items.filter((it) => it.useBadge);
  const fromDashboard = data ? items.some((it) => (it.badge?.(data)?.n ?? 0) > 0) : false;
  return (
    <>
      {fromDashboard && <span className="rail-dot" />}
      {hooked.map((it) => (
        <HookDot key={it.to} use={it.useBadge!} />
      ))}
    </>
  );
}

// Pinned: the page list is a column of its own. Unpinned: it floats beside the rail when a section is
// pressed and gets out of the way once a page is picked. Stored under the old key, so whoever had
// folded the panel away comes back unpinned.
const PANEL_KEY = "keel-nav-panel";
const readPanel = () => {
  try {
    return localStorage.getItem(PANEL_KEY) !== "0";
  } catch {
    return true;
  }
};

export function Shell({ children }: { children: ReactNode }) {
  const { me, can, company } = useAuth();
  const topbar = slotEntries("topbar", me.modules, can);
  const [crumbs, setCrumbs] = useState<Crumb[]>([]);
  const [palette, setPalette] = useState(false);
  const [help, setHelp] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const record = useCurrentRecord();
  const [newOpen, setNewOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(readPanel);
  // Unpinned and showing: the list floats over the page until something closes it.
  const [floating, setFloating] = useState(false);
  const sidebar = useRef<HTMLElement>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme);
  const loc = useLocation();
  const nav = useNavigate();
  const sections = useSections();

  const onOverview = loc.pathname === "/";
  const matched = sectionForPath(sections, loc.pathname);
  // The panel keeps showing something when the route belongs to no section (Overview, My account),
  // so moving between pages never shifts the layout.
  // A floating list shows the section just pressed, wherever the page itself belongs.
  const openGroup = (floating ? picked : null) ?? matched ?? picked ?? sections[0]?.group ?? null;
  const open = sections.find((s) => s.group === openGroup);

  // Sidebar counters from `/dashboard`; only fetched once a nav item asks for one.
  const wantsBadges = sections.some((s) => s.items.some((i) => i.badge));
  const badges = useQuery({
    queryKey: ["dashboard", company.id],
    queryFn: () => api<DashboardBadges>("/dashboard"),
    refetchInterval: 30_000,
    retry: false,
    enabled: wantsBadges,
  });
  const newItems = activePlugins(me.modules)
    .flatMap((p) => p.newMenu ?? [])
    .filter((i) => can(i.perm));

  // biome-ignore lint/correctness/useExhaustiveDependencies: close menus on navigation
  useEffect(() => {
    setNavOpen(false);
    setNewOpen(false);
    setFloating(false);
  }, [loc.pathname]);

  // A floating list closes on a click anywhere outside the sidebar, as a menu would.
  useEffect(() => {
    if (!floating) return;
    const away = (e: MouseEvent) => {
      if (!sidebar.current?.contains(e.target as Node)) setFloating(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [floating]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette(true);
        return;
      }
      const typing =
        (e.target as HTMLElement).closest("input, textarea, select, [contenteditable], [role=listbox]") ||
        document.querySelector("dialog[open]");
      if (typing || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "/") {
        const s = document.querySelector<HTMLInputElement>("[data-search]");
        if (s) {
          e.preventDefault();
          s.focus();
        }
      }
      if (e.key === "[") {
        e.preventDefault();
        togglePanel();
      }
      if (e.key === "n" && newItems.length) {
        e.preventDefault();
        setNewOpen(true);
      }
      if (e.key === "Escape") {
        setNewOpen(false);
        setFloating(false);
      }
      if (newOpen) {
        const go = newItems.find((i) => i.key === e.key);
        if (go) nav(go.to);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const dark = theme === "dark" || (!theme && matchMedia("(prefers-color-scheme: dark)").matches);
  const toggleTheme = () => {
    const next = dark ? "light" : "dark";
    document.documentElement.dataset.theme = next;
    try {
      localStorage.setItem("keel-theme", next);
    } catch {
      /* ignore */
    }
    setTheme(next);
  };
  /** Pin or unpin the page list; the choice outlives the session. */
  const togglePanel = () => {
    setFloating(false);
    setPanelOpen((o) => {
      try {
        localStorage.setItem(PANEL_KEY, o ? "0" : "1");
      } catch {
        /* ignore */
      }
      return !o;
    });
  };

  /**
   * Pinned, picking a section goes to its first page — the list is already on screen, so staying
   * put would leave the click doing nothing. Unpinned, it opens the section's list and lets the
   * page be chosen; pressing the same section again closes it.
   */
  const enter = (s: NavSection) => {
    if (!panelOpen) {
      setFloating((f) => !(f && s.group === openGroup));
      setPicked(s.group);
      return;
    }
    setPicked(s.group);
    if (s.group !== matched) nav(s.items[0]!.to);
  };

  let lastSection: string | undefined;
  return (
    <CrumbCtx.Provider value={setCrumbs}>
      <div
        className={`app ${navOpen ? "nav-open" : ""} ${panelOpen ? "" : "panel-closed"} ${
          floating ? "panel-floating" : ""
        }`}
      >
        <aside className="sidebar" ref={sidebar}>
          <div className="rail">
            <Link to="/" className="rail-brand" title="Keel">
              <svg width="30" height="30" viewBox="0 0 32 32" aria-hidden="true">
                <rect width="32" height="32" rx="9" className="brand-bg" />
                <path d="M8 11h16l-3 9h-10z" className="brand-hull" />
                <path d="M16 20v5" stroke="var(--accent)" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </Link>
            <nav className="rail-nav" aria-label={t("Sections")}>
              <NavLink to="/" end className={({ isActive }) => `rail-item ${isActive ? "active" : ""}`}>
                <Icon name="home" size={19} />
                <span>{t("Overview")}</span>
              </NavLink>
              {sections.map((s) => (
                <button
                  type="button"
                  key={s.group}
                  className={`rail-item ${s.group === matched ? "active" : ""} ${
                    (panelOpen ? !onOverview : floating) && s.group === openGroup ? "showing" : ""
                  }`}
                  onClick={() => enter(s)}
                  aria-current={s.group === matched ? "true" : undefined}
                >
                  <Icon name={s.icon} size={19} />
                  <span>{t(s.group)}</span>
                  <SectionDot items={s.items} data={badges.data} />
                </button>
              ))}
            </nav>
          </div>
          {open && (
            <div className="nav-panel">
              <div className="nav-panel-head">
                <h2>{t(open.group)}</h2>
                <button
                  type="button"
                  className={`icon-btn pin-btn ${panelOpen ? "pinned" : ""}`}
                  onClick={togglePanel}
                  title={`${t(panelOpen ? "Unpin the page list" : "Pin the page list")} ([)`}
                  aria-label={t(panelOpen ? "Unpin the page list" : "Pin the page list")}
                  aria-pressed={panelOpen}
                >
                  <Icon name="pin" size={15} />
                </button>
              </div>
              <nav className="nav">
                {open.items.map((it) => {
                  const b = badges.data && it.badge?.(badges.data);
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      end={it.end}
                      className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                    >
                      <Icon name={it.icon} />
                      <span>{t(it.label)}</span>
                      {b && <span className={`nav-badge ${b.tone ?? ""}`}>{b.n}</span>}
                      {!b && it.useBadge && <HookBadge use={it.useBadge} />}
                    </NavLink>
                  );
                })}
              </nav>
            </div>
          )}
        </aside>
        <div className="main">
          <header className="topbar">
            <button
              type="button"
              className="icon-btn menu-btn"
              onClick={() => setNavOpen((o) => !o)}
              aria-label={t("Menu")}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
            <CompanySwitcher />
            <div className="crumbs">
              {crumbs.map((c, i) =>
                i < crumbs.length - 1 ? (
                  <span key={`${i}-${c.label}`} className="row" style={{ gap: 8 }}>
                    {c.to ? <Link to={c.to}>{c.label}</Link> : <span className="muted">{c.label}</span>}
                    <span className="sep">/</span>
                  </span>
                ) : (
                  <span key={`${i}-${c.label}`} className="here">
                    {c.label}
                  </span>
                ),
              )}
            </div>
            <button type="button" className="search-trigger" onClick={() => setPalette(true)}>
              <Icon name="search" size={15} />
              <span>{t("Search or jump to…")}</span>
              <kbd>⌘K</kbd>
            </button>
            {newItems.length > 0 && (
              <div className="new-menu">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => setNewOpen((o) => !o)}
                >
                  <Icon name="plus" size={14} />
                  {t("New")}
                </button>
                {newOpen && (
                  // biome-ignore lint/a11y/noStaticElementInteractions: hover-close like the prototype
                  <div className="menu" onMouseLeave={() => setNewOpen(false)}>
                    {newItems.map((i) => {
                      const sep = lastSection !== undefined && i.section !== lastSection;
                      lastSection = i.section;
                      return (
                        <span key={i.to} style={{ display: "contents" }}>
                          {sep && <hr />}
                          <Link to={i.to}>
                            {i.label}
                            {i.key && <kbd>{i.key.toUpperCase()}</kbd>}
                          </Link>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {record && <NotesButton record={record} onOpen={() => setNotesOpen(true)} />}
            {topbar.map((b) => (
              <b.component key={b.key} />
            ))}
            <HelpButton onOpen={() => setHelp(true)} />
            <UserMenu dark={dark} toggleTheme={toggleTheme} />
          </header>
          <main id="view" key={company.id}>
            {children}
          </main>
        </div>
        {navOpen && (
          // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click closes the drawer
          // biome-ignore lint/a11y/useKeyWithClickEvents: Escape and the menu button also close it
          <div className="nav-scrim" onClick={() => setNavOpen(false)} />
        )}
      </div>
      {palette && <CommandPalette onClose={() => setPalette(false)} />}
      {help && <HelpDrawer onClose={() => setHelp(false)} />}
      {notesOpen && record && <NotesDrawer record={record} onClose={() => setNotesOpen(false)} />}
      <WhatsNewPopup />
    </CrumbCtx.Provider>
  );
}
